import { FolderOpen } from "lucide-react";

import {
	PROGRAM_COLOR_CLASSES,
	normalizeProgramColor,
	programColorLabelKey,
} from "../../lib/programColors";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * Il colore di un programma o di una cartella, reso in tre forme sole, cosi'
 * si legge allo stesso modo ovunque (lista programmi, blocco «Oggi»,
 * selettori di programma):
 *
 * - `ProgramColorDot`: un pallino in linea col nome;
 * - `ProgramColorStripe`: una barra verticale sul bordo sinistro di una card
 *   (il genitore e' `relative overflow-hidden`);
 * - `ProgramColorFolderIcon`: l'icona di una cartella tinta del suo colore,
 *   come le cartelle colorate del Finder; senza colore e' volt come sempre.
 *
 * Tutti hanno il nome del colore come testo alternativo e come `title`; dot e
 * stripe non rendono nulla senza colore: un programma senza etichetta e'
 * identico a prima. Spec: docs/program-colors/01-analisi.md.
 */

export function ProgramColorFolderIcon({
	color,
	className = "",
	icon: Icon = FolderOpen,
}) {
	const { t } = useLanguage();
	const key = normalizeProgramColor(color);
	const label = key ? t(programColorLabelKey(key)) : null;
	return (
		<Icon
			role={key ? "img" : undefined}
			aria-hidden={key ? undefined : "true"}
			aria-label={label || undefined}
			className={`shrink-0 ${key ? PROGRAM_COLOR_CLASSES[key].text : "text-volt"} ${className}`}
			data-testid="program-color-folder"
			data-color={key || "none"}
		>
			{label ? <title>{label}</title> : null}
		</Icon>
	);
}

export function ProgramColorDot({ color, className = "" }) {
	const { t } = useLanguage();
	const key = normalizeProgramColor(color);
	if (!key) return null;
	const label = t(programColorLabelKey(key));
	return (
		<span
			role="img"
			aria-label={label}
			title={label}
			data-testid="program-color-dot"
			className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${PROGRAM_COLOR_CLASSES[key].bg} ${className}`}
		/>
	);
}

export function ProgramColorStripe({ color }) {
	const { t } = useLanguage();
	const key = normalizeProgramColor(color);
	if (!key) return null;
	const label = t(programColorLabelKey(key));
	return (
		<span
			role="img"
			aria-label={label}
			title={label}
			data-testid="program-color-stripe"
			className={`absolute inset-y-0 left-0 w-1 ${PROGRAM_COLOR_CLASSES[key].bg}`}
		/>
	);
}
